import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  RefreshCw,
  TrendingUp,
  BarChart,
  LineChart,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button, Alert } from "@mui/material";
import SearchAndFilters from "../../../components/SearchAndFilters";
import SearchContentContainer from "../../../components/SearchContentContainer";
import { filterConfigs } from "../../../components/SearchAndFilters/config";
import LoadingSkeleton from "../../../components/LoadingSkeleton";
import { InitialSearchStates } from "../../../components/SearchAndFilters/searchStates";
import useWebSocket from "../../../utils/Sockets";
import CompetitiveAnalysisService from "../../../services/CompetitiveAnalysis";
import GenerationProgress from "../../../components/GenerationProgress";
import queryString from "query-string";
import { homeRoutePath, settingsPath } from "../../Router";
import SessionService from "../../../services/Session";
import { setUser } from "../../../redux/actions/user";
import { useDispatch } from "react-redux";
import { config } from "../../../config";
import { logIssueNetworkRequestError } from "../../../utils/Loggers";
import CompetitorDataDialog from "./CompetitorDataDialog";
import { displayToastMessage } from "../../../utils/Toasts";
import { useSubscriptionLimit } from "../../../hooks/useSubscriptionLimit";
import SubscriptionLimitDialog from "../../../components/SubscriptionLimitDialog";

const AnalysisCard = ({ analysis, onClick, index }: any): any => {
  const [parsedData, setParsedData] = useState<any>({
    initialAnalysis: null,
    competitorAnalysis: null,
    recommendations: null,
  });

  useEffect(() => {
    try {
      setParsedData({
        initialAnalysis: JSON.parse(analysis.analysis.initialAnalysis),
        competitorAnalysis: JSON.parse(analysis.analysis.competitorAnalysis),
        recommendations: JSON.parse(analysis.analysis.recommendations),
      });
    } catch (error: Error | any) {
      console.error("Error parsing analysis data:", error);
    }
  }, [analysis]);

  return (
    <motion.div
      className="h-full rounded-xl bg-white border border-gray-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 break-words"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      onClick={() => onClick(analysis)}
    >
      {/* Card with gradient top border */}
      <div className="relative h-full flex flex-col overflow-hidden break-words">
        {/* Gradient top border */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-600 to-gray-900"></div>

        {/* Main content container */}
        <div className="p-4 sm:p-6 flex flex-col h-full break-words">
          {/* Header section with improved visual hierarchy */}
          <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
            {/* Title and timestamp */}
            <div className="break-words">
              <h3 className="text-base sm:text-lg font-semibold mb-1 line-clamp-2 group-hover:text-red-600 transition-colors break-words">
                {analysis.name}
              </h3>
              <div className="flex items-center justify-between text-xs sm:text-sm break-words">
                <p className="text-gray-500 break-words">
                  {parsedData.competitorAnalysis?.competitors?.length || 0}{" "}
                  Competitors Analyzed
                </p>
                <p className="text-gray-400 break-words">
                  {new Date(analysis.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Key Findings badge with improved styling */}
            <div className="flex justify-start break-words">
              <div className="bg-red-50 text-red-700 px-2 py-1 rounded-full text-xs sm:text-sm font-medium break-words">
                {parsedData.initialAnalysis?.summary?.keyFindings?.length || 0}{" "}
                Key Findings
              </div>
            </div>
          </div>

          {/* Stats grid with improved visualization and icons */}
          <div className="grid grid-cols-1 sm:grid-cols-1 gap-3 sm:gap-4 mb-4 sm:mb-6 break-words">
            <div className="flex items-center gap-2 p-2 sm:p-3 bg-gray-50 rounded-lg break-words">
              <div className="p-1.5 sm:p-2 rounded-full bg-red-100 break-words">
                <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-red-600" />
              </div>
              <div className="flex-1 overflow-hidden break-words">
                <p className="text-gray-500 text-[10px] sm:text-xs truncate break-words">
                  Market Position
                </p>
                <p className="font-medium text-xs sm:text-sm line-clamp-1 break-words">
                  {parsedData.initialAnalysis?.summary?.overallPosition?.split(
                    "."
                  )[0] || "N/A"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 p-2 sm:p-3 bg-gray-50 rounded-lg break-words">
              <div className="p-1.5 sm:p-2 rounded-full bg-gray-100 break-words">
                <BarChart className="h-3 w-3 sm:h-4 sm:w-4 text-gray-600" />
              </div>
              <div className="flex-1 overflow-hidden break-words">
                <p className="text-gray-500 text-[10px] sm:text-xs truncate break-words">
                  Critical Actions
                </p>
                <p className="font-medium text-xs sm:text-sm break-words">
                  {parsedData.initialAnalysis?.summary?.criticalActions
                    ?.length || 0}{" "}
                  Actions
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 p-2 sm:p-3 bg-gray-50 rounded-lg break-words">
              <div className="p-1.5 sm:p-2 rounded-full bg-red-100 break-words">
                <LineChart className="h-3 w-3 sm:h-4 sm:w-4 text-red-600" />
              </div>
              <div className="flex-1 overflow-hidden break-words">
                <p className="text-gray-500 text-[10px] sm:text-xs truncate break-words">
                  Opportunities
                </p>
                <p className="font-medium text-xs sm:text-sm break-words">
                  {parsedData.recommendations?.strategicPriorities?.length || 0}{" "}
                  Identified
                </p>
              </div>
            </div>
          </div>

          {/* Footer section with improved hover state */}
          <div className="flex items-center gap-2 text-red-600 mt-auto pt-4 border-t border-gray-100 break-words">
            <span className="text-xs sm:text-sm font-medium break-words">
              View Analysis
            </span>
            <svg
              className="w-3 h-3 sm:w-4 sm:h-4 transition-transform group-hover:translate-x-1 break-words"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const AnalysisList = ({ data, onAnalysisSelect }: any) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 p-2 sm:p-0">
      <AnimatePresence>
        {data.map((analysis: any, index: number) => (
          <AnalysisCard
            key={analysis._id}
            analysis={analysis}
            index={index}
            onClick={onAnalysisSelect}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

const CompetitiveAnalysisView = ({ onBack }: any) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userId, setUserId] = useState("");
  const [analyses, setAnalyses] = useState<any>([]);
  const [searchState, setSearchState] = useState(
    InitialSearchStates.competitiveAnalysis
  );
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generationParams, setGenerationParams] = useState<any>(null);

  const fetchAnalyses = useCallback(
    async (userId: string | any, searchParams: any) => {
      setLoading(true);
      setError("");

      try {
        if (!config.inProd) {
          // Use mock data
          setAnalyses(config.mocks.competitiveAnalysisReports);
          return;
        }

        const service = new CompetitiveAnalysisService();
        const response = await service.listAnalyses(projectId, userId, {
          name: searchParams.query,
          page: searchParams?.pagination?.currentPage,
          limit: searchParams?.pagination?.limit,
          sortBy: searchParams?.sorting?.sortBy || "createdAt",
          sortOrder: searchParams?.sorting?.sortOrder === -1 ? "desc" : "asc",
          dateFrom: searchParams?.filters?.dateFrom,
          dateTo: searchParams?.filters?.dateTo,
        });

        setAnalyses(response.analyses);
        setSearchState((prevState) => ({
          ...prevState,
          pagination: response.pagination,
        }));
      } catch (error: Error | any) {
        console.error("Error fetching analyses:", error);
        setError(error.message || "Failed to load analyses");
      } finally {
        setLoading(false);
      }
    },
    [projectId]
  );

  // Progress and generation state
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generationStage, setGenerationStage] = useState("");
  const [generationError, setGenerationError] = useState("");
  const [jobState, setJobState] = useState<any>(null);
  const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState("");

  const { dialogProps, makeRequest: makeSubscriptionGatedRequest } =
    useSubscriptionLimit(() => navigate(settingsPath, { replace: true }));

  const onProgress = useCallback((data: any) => {
    if (data.jobType === "competitiveAnalysis") {
      setProgress(data.progress);
      setGenerationStage(data.stage);
      setLastUpdateTimestamp(data.timestamp);
      setJobState("in-progress");
      if (data.progress === 100) {
        setJobState("completed");
        setGenerating(false);
      }
    }
  }, []);

  const onJobCompleted = useCallback(
    (data: any) => {
      if (data.type === "competitiveAnalysis") {
        setProgress(100);
        setJobState("completed");
        setGenerationStage("Analysis Generation Complete");
        setLastUpdateTimestamp(new Date().toISOString());
        setGenerating(false);
        window.location.reload();
      }
    },
    [userId, searchState]
  );

  const { socket, connected } = useWebSocket({
    onProgress,
    onJobCompleted,
    onJobFailed: (error) => {
      setProgress(-1);
      setJobState("error");
      setGenerationError(error.message);
      setLastUpdateTimestamp(new Date().toISOString());
      setGenerating(false);
    },
  });

  const handleGenerateClick = () => {
    setGenerationError("");
    setShowGenerateDialog(true);
  };

  const handleGenerateSubmit = async (params: any) => {
    setShowGenerateDialog(false);
    setJobState("initializing");
    setProgress(0);
    setGenerationStage("Initializing Competitive Analysis");
    setLastUpdateTimestamp(new Date().toISOString());
    setGenerationError("");
    setGenerating(true);

    const service = new CompetitiveAnalysisService();

    await makeSubscriptionGatedRequest({
      requestFn: () =>
        service.startAnalysisGeneration(projectId, {
          userId,
          socketId: socket?.id,
          priority: 5,
          modelAPIKey: params.modelAPIKey,
          icpId: params.icpId,
          competitorData: params.competitorData,
        }),
      onSuccess: (_) => {
        displayToastMessage(
          "success",
          "Analysis generation successfully started"
        );
      },
      onError: (error: any) => {
        console.error("Failed to start analysis generation", error);
        let errorMsg = error.error ? error.error : "";
        setJobState("error");
        setGenerationError(
          `Failed to start analysis generation. Please try again. Error: ${errorMsg}`
        );
        setProgress(-1);
        setLastUpdateTimestamp(new Date().toISOString());
      },
    });
  };

  const handleInitialLoad = useCallback(async () => {
    try {
      const parsedQuery = queryString.parse(location.search);
      if (parsedQuery.email && parsedQuery.view === "activate") {
        navigate(homeRoutePath, { replace: true });
        return;
      }

      const sessionService = new SessionService();
      const sessionData = await sessionService.reviewSession();
      if (!sessionData.success) {
        navigate(homeRoutePath, { replace: true });
        return;
      }

      dispatch(setUser(sessionData.userSessionData));
      setUserId(sessionData.userSessionData.id);

      await fetchAnalyses(sessionData.userSessionData.id, searchState);
    } catch (error: Error | any) {
      logIssueNetworkRequestError(
        "CompetitiveAnalysisView.handleInitialLoad",
        error
      );
      navigate(homeRoutePath, { replace: true });
    }
  }, [dispatch, location, navigate, fetchAnalyses]);

  useEffect(() => {
    handleInitialLoad();
  }, [handleInitialLoad]);

  const handleSearch = async (searchParams: any) => {
    setSearchState(searchParams);
    await fetchAnalyses(userId, searchParams);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header Section with enhanced styling */}
        <div className="mb-6 bg-white p-5 rounded-xl shadow-sm border border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (onBack) onBack();
                  navigate(`/project/${projectId}`);
                }}
                className="p-2 rounded-full text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Competitive Analysis
                </h1>
                <p className="text-sm text-gray-500">
                  Analyze your competitors and identify market opportunities
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-4 sm:mt-0">
              <Button
                variant="outlined"
                startIcon={<RefreshCw className="h-4 w-4" />}
                onClick={() => fetchAnalyses(userId, searchState)}
                className="text-gray-700 border-gray-300 hover:bg-gray-50"
                sx={{
                  borderRadius: "8px",
                  padding: "8px 16px",
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Plus className="h-4 w-4" />}
                onClick={handleGenerateClick}
                disabled={generating}
                className="bg-red-600 hover:bg-red-700"
                sx={{
                  background: "linear-gradient(to right, #E11D48, #BE123C)",
                  boxShadow: "0 4px 6px -1px rgba(225, 29, 72, 0.2)",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": {
                    background: "linear-gradient(to right, #BE123C, #9F1239)",
                  },
                }}
              >
                Generate New Analysis
              </Button>
            </div>
          </div>
        </div>

        {/* Error Alert with improved styling */}
        {error && (
          <Alert
            severity="error"
            className="mb-6 rounded-lg border border-red-200 shadow-sm"
            sx={{
              "& .MuiAlert-icon": {
                color: "#EF4444",
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Generation Progress with improved styling */}
        {jobState && (
          <div className="mb-6 bg-white p-5 rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <GenerationProgress
              title="Analyzing Competitive Data"
              description="Please wait while we analyze your competitors and generate insights..."
              progress={progress}
              stage={generationStage}
              timestamp={lastUpdateTimestamp}
              jobType="competitiveAnalysis"
              jobState={jobState}
              error={generationError}
            />
          </div>
        )}

        {/* Search and Filters Section with improved styling */}
        <div className="mb-6 bg-white p-5 rounded-xl shadow-sm border border-gray-200">
          <SearchAndFilters
            totalShown={analyses.length}
            onSearch={handleSearch}
            pagination={searchState.pagination}
            sorting={searchState.sorting}
            filters={searchState.filters}
            filterConfig={filterConfigs.competitiveAnalysis}
            isLoading={loading}
          />
        </div>

        {/* Content Section with improved styling */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
          <SearchContentContainer
            isLoading={loading}
            data={analyses}
            LoadingComponent={LoadingSkeleton}
            ContentComponent={AnalysisList}
            componentProps={{
              onAnalysisSelect: (analysis: any) =>
                navigate(
                  `/project/${projectId}/competitive-analysis/${analysis._id}`
                ),
            }}
            loadingText="Loading analyses..."
            emptyTitle="No Analyses Found"
            emptyMessage="Try adjusting your search or filter criteria, or generate a new analysis"
          />
        </div>
      </div>

      <CompetitorDataDialog
        open={showGenerateDialog}
        onClose={() => setShowGenerateDialog(false)}
        onSubmit={handleGenerateSubmit}
        loading={generating}
        error={generationError}
      />

      <SubscriptionLimitDialog {...dialogProps} />
    </div>
  );
};

export default CompetitiveAnalysisView;
